import { reaction, runInAction } from 'mobx';
import { TICKERS_CATEGORIES } from 'common/constants/tickers.constants';
import { PRICING_TYPES } from 'common/constants/socket.constants';
import { first } from 'common/utils/common.utils';
import { stores } from 'stores';
import { BackendError, captureError } from '../../utils/error.util';

export class QuotesService {
  account;

  extension;

  subscriptions = [];

  constructor(account, extension) {
    this.account = account;
    this.extension = extension;
  }

  #handleLastPrices(symbols) {
    const msg = {
      type: PRICING_TYPES.LAST_TICKS,
      body: { symbol: symbols.join() },
      callback: (tick) => this.account.handleLastPrices(tick),
    };

    if (symbols.length !== 0) this.account.pricingSocket.send(msg);
  }

  #handleTicks(subList, unsubList) {
    const subMsg = {
      type: PRICING_TYPES.TICKS_SUBSCRIBE,
      body: { symbol: subList.join(',') },
      callback: (tick) => this.account.handleTickReceive(tick),
    };

    const unSubMsg = {
      type: PRICING_TYPES.TICKS_UNSUBSCRIBE,
      body: { symbol: unsubList.join(',') },
    };

    if (subList.length > 0) this.account.tradingSocket.send(subMsg);
    if (unsubList.length > 0) this.account.tradingSocket.send(unSubMsg);
  }

  #handleCandles(symbols) {
    try {
      symbols.forEach((symbol) => {
        this.account.pricingSocket.send({
          type: PRICING_TYPES.GET_CANDLES,
          body: {
            symbol,
            from: +new Date() - 60 * 60 * 1000,
            to: +new Date() - 50 * 60 * 1000,
            time_frame: 1,
          },
          callback: (candles) => {
            if (candles.length) {
              runInAction(() => {
                this.account.lastHourCandles[symbol] = first(candles);
              });
            }
          },
        });
      });
    } catch (err) {
      captureError(new BackendError(err), true);
    }
  }

  deliver() {
    const { account } = this;
    let symbolsToObserve = [
      ...TICKERS_CATEGORIES[account.selectedCategory].symbols,
      ...account.openOrders.map((order) => order.symbol),
    ].map((s) => `${s}${this.extension}`);

    const favorites = stores.ProfileStore.metaData?.favoriteInstruments || [];

    symbolsToObserve = [...symbolsToObserve, ...favorites];

    const ticksAwaitList = symbolsToObserve.filter(
      (s) => !Object.keys(account.lastTick).includes(s)
    );

    const ticksSubList = symbolsToObserve.filter(
      (s) => !this.subscriptions.includes(s) && s !== account.selectedSymbolName
    );

    const ticksUnsubList = this.subscriptions.filter(
      (s) => !symbolsToObserve.includes(s) && s !== account.selectedSymbolName
    );

    this.subscriptions = ticksSubList;

    const symbolsWithCandles = Object.keys(account.lastHourCandles);
    const symbolsWithoutCandles = symbolsToObserve.filter(
      (s) => !symbolsWithCandles.includes(s)
    );

    reaction(
      () => account.isPricingSocketInitiated,
      (connected) => {
        if (connected) {
          this.#handleCandles(symbolsWithoutCandles);
          this.#handleLastPrices(ticksAwaitList);
        }
      },
      {
        fireImmediately: true,
      }
    );

    reaction(
      () => account.isTradingSocketConnected,
      (connected) => {
        if (connected) this.#handleTicks(ticksSubList, ticksUnsubList);
      },
      {
        fireImmediately: true,
      }
    );
  }
}
